/**
 * scanner.js — QR / barcode scan abstraction.
 *
 * On native, uses `@capacitor-mlkit/barcode-scanning` which puts the
 * WebView into a transparent overlay and streams frames from the OS
 * camera. The scanner is short-lived (one scan → stop) and the caller
 * MUST call `stop()` on unmount so the camera and status-bar restore
 * cleanly. See usage from `ChargerEntry` for the reference pattern.
 *
 * On web the abstraction returns `unsupported`; the existing
 * manual-code entry field remains the fallback. We deliberately don't
 * ship a browser polyfill (jsQR + WebRTC) because the customer app has
 * a first-class manual entry path already.
 *
 * A scan is validated against the GreenCharge charger-code format
 * (`GCEV` + 7 digits) before the promise resolves — arbitrary QR
 * payloads never trigger navigation.
 */
import { isNative } from './platform.js';
import { hide as hideStatusBar, show as showStatusBar } from './statusBar.js';
import { CHARGER_CODE_RE } from '../utils/chargerCode.js';

let _scanner = null;
async function scannerPlugin() {
  if (_scanner) return _scanner;
  const mod = await import('@capacitor-mlkit/barcode-scanning');
  _scanner = mod.BarcodeScanner;
  return _scanner;
}

/** True when native scanning is available in the current runtime. */
export function isNativeScanAvailable() {
  return isNative();
}

/**
 * Extract the canonical charger code from a scanned payload. Accepts:
 *   - Bare code:               `GCEV0920879`
 *   - Full URL:                `https://greencharge.app/c/GCEV0920879`
 *   - Custom scheme deep link: `greencharge://c/GCEV0920879`
 * Returns `null` when the payload is not a valid GreenCharge charger.
 */
export function extractChargerCode(payload) {
  if (typeof payload !== 'string') return null;
  const trimmed = payload.trim();
  if (!trimmed) return null;
  if (CHARGER_CODE_RE.test(trimmed)) return trimmed;
  // Try URL parse for the two supported link shapes.
  try {
    const parsed = new URL(trimmed);
    const parts = parsed.pathname.split('/').filter(Boolean);
    const code = parts[parts.length - 1];
    if (code && CHARGER_CODE_RE.test(code)) return code;
  } catch { /* not a URL — fall through */ }
  return null;
}

/**
 * Run the native scanner until it finds a supported charger code or the
 * caller cancels. Resolves with:
 *   { ok: true,  code: 'GCEV1234567' }
 *   { ok: false, reason: 'cancelled' | 'permission_denied' | 'unsupported' | 'error', message? }
 *
 * Consumers should show a loading / camera-permission UX around the
 * call — the scan UI itself is drawn by the OS.
 */
export async function scanChargerCode() {
  if (!isNative()) {
    return { ok: false, reason: 'unsupported' };
  }
  const scanner = await scannerPlugin().catch(() => null);
  if (!scanner) return { ok: false, reason: 'unsupported' };

  // The plugin needs a Google MLKit module bundle on Android. On some
  // installs it's shipped alongside the app; on others it needs an
  // async download. Guard both paths.
  try {
    const modules = await scanner.isGoogleBarcodeScannerModuleAvailable?.();
    if (modules && modules.available === false) {
      // First run — kick a background install and treat this scan as
      // unsupported. The next tap will find the module ready.
      try { await scanner.installGoogleBarcodeScannerModule?.(); } catch { /* ignore */ }
      return { ok: false, reason: 'error', message: 'scanner_module_installing' };
    }
  } catch { /* older plugin version — safe to skip the check */ }

  // Hide the status bar and let the app go behind the camera preview.
  document.body.classList.add('barcode-scanner-active');
  await hideStatusBar();

  const cleanup = async () => {
    document.body.classList.remove('barcode-scanner-active');
    try { await scanner.stopScan(); } catch { /* ignore */ }
    try { await scanner.removeAllListeners(); } catch { /* ignore */ }
    await showStatusBar();
  };

  try {
    // Prefer the streaming `scan()` API (preview + listener) when the
    // plugin exposes it, falling back to the single-shot `scan()` on
    // older versions. Both resolve with a `{ barcodes: [] }` shape.
    const result = await scanner.scan({ formats: ['QR_CODE'] });
    const first = result?.barcodes?.[0];
    const raw = first?.displayValue || first?.rawValue || '';
    const code = extractChargerCode(raw);
    await cleanup();
    if (!code) {
      return { ok: false, reason: 'error', message: 'invalid_charger_code' };
    }
    return { ok: true, code };
  } catch (err) {
    await cleanup();
    const message = err?.message || 'scan_failed';
    if (/cancel/i.test(message)) return { ok: false, reason: 'cancelled' };
    if (/denied|permission/i.test(message)) return { ok: false, reason: 'permission_denied' };
    return { ok: false, reason: 'error', message };
  }
}
